// 가입·로그인 페이지 묶음 — 공개(인증 전) 페이지 3종.
//
//  - renderInviteActivate: 운영진이 보낸 단건 초대 링크. 본인 PIN을 처음
//    설정하고 가입 완료. 회장 초대면 운영자 동의 박스가 추가로 뜸.
//  - renderRosterJoin: 명단 일괄 초대. 본인 이름 선택 → 성별/PIN 입력.
//    동의 처리 + roster-claim API.
//  - renderMemberLogin: PIN 로그인 화면. pendingRoute가 있으면 거기로 복귀.
//
// 인증 흐름 도메인이라 한 모듈로 묶음. showWelcomeWithLoginName은 활성화/
// 명단가입 두 페이지가 공유하는 가입 완료 다이얼로그.

import { Fragment } from "preact";
import { useRef, useState } from "preact/hooks";
import { api, safeJson } from "../api";
import { levelOptionsForGender } from "./index";
import { goToClubHome, navigate } from "../routes";
import { DEFAULT_CONFIG, persist, state } from "../state";
import { Icon } from "../ui/components/Icon";
import { mountPreact } from "../ui/preact-mount";
import { closeSheet, openSheet, toast, withBusy } from "../ui/sheet";

// ===== Privacy / Operator consent sheets =====
function PrivacyConsentBody() {
  return (
    <div class="stack" style="font-size:14px;line-height:1.6">
      <p>페어매치는 클럽 운영 지원을 위해 다음 정보를 수집합니다.</p>
      <p style="margin-top:8px;font-weight:600">수집 항목</p>
      <ul style="margin:4px 0 0 16px">
        <li>이름, 성별, 레벨 (필수)</li>
        <li>NTRP, 연락처, 선호 코트, 주 사용 손 (선택)</li>
        <li>경기 기록 — 승/패/무, 파트너, 상대 (자동 생성)</li>
      </ul>
      <p style="margin-top:8px;font-weight:600">이용 목적</p>
      <ul style="margin:4px 0 0 16px">
        <li>참가자 식별 및 대진표 생성</li>
        <li>경기 결과 기록 및 통계</li>
      </ul>
      <p style="margin-top:8px;font-weight:600">보유 기간</p>
      <p>클럽 가입 기간 동안 보유하며, 탈퇴 또는 삭제 요청 시 즉시 파기합니다.</p>
      <p style="margin-top:8px">
        전체 처리방침 문의:{" "}
        <a href="mailto:fairmatch.team@gmail.com" style="color:var(--accent)">fairmatch.team@gmail.com</a>
      </p>
    </div>
  );
}

function OperatorConsentBody() {
  return (
    <div class="stack" style="font-size:14px;line-height:1.6">
      <p>운영진은 클럽 회원의 개인정보를 등록·관리하는 처리자 역할을 합니다. 페어매치팀은 위탁을 받아 시스템을 제공합니다.</p>
      <p style="margin-top:8px;font-weight:600">운영자의 의무</p>
      <ul style="margin:4px 0 0 16px">
        <li>회원에게 개인정보 수집·이용 사실을 고지</li>
        <li>등록하는 회원 정보의 정확성 확인</li>
        <li>회원의 정보 삭제 요청 시 이행</li>
      </ul>
      <p style="margin-top:8px;font-weight:600">페어매치팀의 의무</p>
      <ul style="margin:4px 0 0 16px">
        <li>클럽 운영 목적 외 사용 금지</li>
        <li>제3자 제공 금지</li>
        <li>HTTPS 암호화 및 접근 제어 적용</li>
        <li>탈퇴·서비스 종료 시 데이터 파기</li>
      </ul>
      <p style="margin-top:8px">
        문의:{" "}
        <a href="mailto:fairmatch.team@gmail.com" style="color:var(--accent)">fairmatch.team@gmail.com</a>
      </p>
    </div>
  );
}

function openPrivacySheet() {
  openSheet("개인정보 처리방침", mountPreact(<PrivacyConsentBody />));
}

function openOperatorSheet() {
  openSheet("클럽 운영자 동의", mountPreact(<OperatorConsentBody />));
}

// ===== Shared building blocks =====

function ClubIntroCard({ verified }: { verified: any }) {
  return (
    <div class="club-intro-card">
      {verified.club_logo_url ? (
        <img class="club-intro-card__logo" src={verified.club_logo_url} alt="club logo" />
      ) : (
        <div class="club-intro-card__logo club-intro-card__logo--empty">
          {(verified.club_short_name || verified.club_name || "🎾").slice(0, 1)}
        </div>
      )}
      <div class="club-intro-card__body">
        <p class="club-intro-card__name">{verified.club_name || "테니스 클럽"}</p>
        {verified.club_region && <p class="club-intro-card__region">{verified.club_region}</p>}
        {verified.club_description && <p class="club-intro-card__desc">{verified.club_description}</p>}
      </div>
    </div>
  );
}

function PrivacyCheckbox({
  id,
  checked,
  onChange,
}: {
  id: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div class="field">
      <label class="consent-row" style="display:flex;align-items:flex-start;gap:8px;cursor:pointer">
        <input
          type="checkbox"
          id={id}
          checked={checked}
          style="margin-top:3px;flex-shrink:0"
          onChange={(e) => onChange((e.currentTarget as HTMLInputElement).checked)}
        />
        <span class="field__hint" style="margin:0">
          개인정보 수집·이용에 동의합니다.{" "}
          <a
            href="#"
            style="color:var(--accent);text-decoration:underline"
            onClick={(e) => { e.preventDefault(); openPrivacySheet(); }}
          >
            내용 보기
          </a>
        </span>
      </label>
    </div>
  );
}

function OperatorCheckbox({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div class="field">
      <label class="consent-row" style="display:flex;align-items:flex-start;gap:8px;cursor:pointer">
        <input
          type="checkbox"
          id="operator-consent"
          checked={checked}
          style="margin-top:3px;flex-shrink:0"
          onChange={(e) => onChange((e.currentTarget as HTMLInputElement).checked)}
        />
        <span class="field__hint" style="margin:0">
          클럽 운영자로서 개인정보 처리 위탁에 동의합니다.{" "}
          <a
            href="#"
            style="color:var(--accent);text-decoration:underline"
            onClick={(e) => { e.preventDefault(); openOperatorSheet(); }}
          >
            내용 보기
          </a>
        </span>
      </label>
    </div>
  );
}

function ChipPicker({
  options,
  value,
  onChange,
}: {
  options: string[];
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  return (
    <div class="chip-row">
      {options.map((opt) => {
        const sel = opt === value;
        return (
          <button
            key={opt}
            type="button"
            class={"chip" + (sel ? " chip--selected" : "")}
            onClick={() => onChange(sel ? null : opt)}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

// ===== Welcome dialog =====

function WelcomeBody({ loginName }: { loginName: string }) {
  return (
    <div class="stack">
      <div class="card">
        <p class="card__lead">가입이 완료됐어요! 환영해요.</p>
        <p class="card__sub" style="margin-top:10px;line-height:1.6">
          앞으로 로그인할 때 이름은
          <br />
          <strong style="font-size:18px;color:var(--accent)">"{loginName}"</strong>
          <br />
          입니다. 이 이름 그대로 입력해주세요.
        </p>
      </div>
      <button
        class="btn btn--primary btn--block"
        type="button"
        onClick={() => { closeSheet(); goToClubHome(); navigate({ name: "home" }); }}
      >
        확인하고 시작하기
      </button>
    </div>
  );
}

function showWelcomeWithLoginName(loginName: string): void {
  openSheet("가입 완료", mountPreact(<WelcomeBody loginName={loginName} />));
}

// =====================================================================
// renderInviteActivate
// =====================================================================

function InviteActivateForm({
  verified,
  slug,
  memberId,
  token,
}: {
  verified: any;
  slug: string;
  memberId: string;
  token: string;
}) {
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [level, setLevel] = useState<string | null>(verified.level_category || null);
  const [privacyOk, setPrivacyOk] = useState(false);
  const [operatorOk, setOperatorOk] = useState(false);

  const isPresident = verified.app_role === "president";
  const levelOptions = levelOptionsForGender(verified.gender);

  async function onSubmit(e: Event) {
    e.preventDefault();
    if (!privacyOk) { toast("개인정보 수집·이용에 동의해주세요"); return; }
    if (isPresident && !operatorOk) { toast("클럽 운영자 동의가 필요해요"); return; }
    if (!/^\d{6}$/.test(pin)) { toast("PIN 6자리(숫자)를 입력해주세요"); return; }
    if (pin !== confirmPin) { toast("두 PIN이 서로 달라요"); return; }
    if (/^(.)\1+$/.test(pin)) { toast("너무 단순한 PIN이에요"); return; }
    try {
      const body: any = { token, new_pin: pin };
      if (level) body.level_category = level;
      const res = await api(
        `/api/v1/clubs/${slug}/invite/${memberId}/activate`,
        { method: "POST", body },
      );
      state.session = res.auth;
      if (res.club) state.club = res.club;
      persist();
      showWelcomeWithLoginName(res.auth?.actor?.name || verified.name);
    } catch (err: any) {
      if (err.code === "INVITE_EXPIRED") toast("링크 유효 기간이 지났어요. 운영진에게 재발급 요청해주세요");
      else if (err.code === "INVALID_PIN") toast("PIN은 6자리 숫자로 설정해주세요");
      else toast(err.message);
    }
  }

  return (
    <form class="form" onSubmit={onSubmit}>
      <div class="field">
        <span class="field__label">새 PIN</span>
        <input
          class="input"
          type="password"
          inputmode="numeric"
          autocomplete="new-password"
          maxLength={6}
          placeholder="새 PIN 6자리"
          value={pin}
          onInput={(e) => setPin((e.currentTarget as HTMLInputElement).value)}
        />
        <span class="field__hint">앞으로 이 PIN으로 로그인해요. 6자리 숫자.</span>
      </div>
      <div class="field">
        <span class="field__label">PIN 확인</span>
        <input
          class="input"
          type="password"
          inputmode="numeric"
          autocomplete="new-password"
          maxLength={6}
          placeholder="한번 더 입력"
          value={confirmPin}
          onInput={(e) => setConfirmPin((e.currentTarget as HTMLInputElement).value)}
        />
      </div>
      {levelOptions.length > 0 && (
        <div class="field">
          <span class="field__label">레벨 (선택)</span>
          <ChipPicker options={levelOptions} value={level} onChange={setLevel} />
          <span class="field__hint">나중에 프로필에서 바꿀 수 있어요.</span>
        </div>
      )}
      <PrivacyCheckbox id="privacy-consent" checked={privacyOk} onChange={setPrivacyOk} />
      {isPresident && <OperatorCheckbox checked={operatorOk} onChange={setOperatorOk} />}
      <button class="btn btn--primary btn--block" type="submit">가입 완료</button>
    </form>
  );
}

function InviteActivateView({
  verified,
  slug,
  memberId,
  token,
}: {
  verified: any;
  slug: string;
  memberId: string;
  token: string;
}) {
  const clubName = verified.club_name || state.club?.name || "";
  return (
    <Fragment>
      <ClubIntroCard verified={verified} />
      <div class="card">
        {clubName && <p class="card__eyebrow">{clubName} 초대</p>}
        <p class="card__lead">{verified.name}님, 환영해요!</p>
        <p class="card__sub">
          {clubName
            ? `${clubName} 앱에서 사용할 본인 PIN 6자리를 설정해주세요.`
            : "앞으로 사용할 본인 PIN 6자리를 설정해주세요."}
        </p>
        <InviteActivateForm verified={verified} slug={slug} memberId={memberId} token={token} />
      </div>
    </Fragment>
  );
}

export async function renderInviteActivate(
  view: HTMLElement,
  memberId: string,
  token: string,
): Promise<void> {
  const slug = state.config.clubSlug;
  view.innerHTML = "";
  if (!slug || !memberId || !token) {
    view.append(mountPreact(<div class="error-card">초대 링크가 올바르지 않아요.</div>));
    return;
  }

  const loading = document.createElement("div");
  loading.className = "loading";
  loading.textContent = "초대 확인 중…";
  view.append(loading);

  let verified: any;
  try {
    verified = await api(
      `/api/v1/clubs/${slug}/invite/${memberId}/verify`,
      { method: "POST", body: { token } },
    );
  } catch (err: any) {
    view.innerHTML = "";
    if (err.code === "INVITE_EXPIRED") {
      view.append(mountPreact(
        <div class="error-card">
          <h2>링크가 만료됐어요</h2>
          <p>운영진에게 초대 링크 재발급을 요청해주세요.</p>
        </div>,
      ));
    } else if (err.code === "INVITE_NOT_FOUND" || err.code === "INVALID_INVITE") {
      view.append(mountPreact(
        <div class="error-card">
          <h2>유효하지 않은 링크예요</h2>
          <p>링크를 다시 확인하거나 운영진에게 문의해주세요.</p>
        </div>,
      ));
    } else {
      view.append(mountPreact(<div class="error-card">{err.message}</div>));
    }
    return;
  }

  view.innerHTML = "";
  view.append(mountPreact(
    <InviteActivateView verified={verified} slug={slug} memberId={memberId} token={token} />,
  ));
}

// =====================================================================
// renderRosterJoin
// =====================================================================

function RosterClaimForm({
  member,
  slug,
  token,
}: {
  member: any;
  slug: string;
  token: string;
}) {
  const [gender, setGender] = useState<"male" | "female" | null>(null);
  const [level, setLevel] = useState<string | null>(null);
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [privacyOk, setPrivacyOk] = useState(false);
  const submitRef = useRef<HTMLButtonElement>(null);

  const levelOpts = gender ? levelOptionsForGender(gender) : [];

  function pickGender(v: "male" | "female") {
    setGender((prev) => {
      const next = prev === v ? null : v;
      if (next !== prev) setLevel(null);
      return next;
    });
  }

  async function onSubmit(e: Event) {
    e.preventDefault();
    if (!gender) { toast("성별을 선택해주세요"); return; }
    if (!privacyOk) { toast("개인정보 수집·이용에 동의해주세요"); return; }
    if (!/^\d{6}$/.test(pin)) { toast("PIN 6자리(숫자)를 입력해주세요"); return; }
    if (pin !== confirmPin) { toast("두 PIN이 서로 달라요"); return; }
    if (/^(.)\1+$/.test(pin)) { toast("너무 단순한 PIN이에요"); return; }
    try {
      await withBusy(submitRef.current, "가입 중…", async () => {
        const base = (state.config.apiBase || DEFAULT_CONFIG.apiBase).replace(/\/$/, "");
        const url = `${base}/api/v1/public/clubs/${encodeURIComponent(slug)}/roster-claim`;
        const body = {
          token,
          member_id: member.member_id,
          new_pin: pin,
          gender,
          privacy_consent: true,
          level_category: level || null,
        };
        const resp = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const text = await resp.text();
        const payload = text ? safeJson(text) : null;
        if (!resp.ok) {
          const errBody = (payload && (payload.error || payload)) || {};
          const e: any = new Error(errBody.message || `요청 실패 (${resp.status})`);
          e.code = errBody.code;
          throw e;
        }
        const res = payload.data || payload;
        state.session = res.auth;
        if (res.club) state.club = res.club;
        persist();
        closeSheet();
        showWelcomeWithLoginName(res.auth?.actor?.name || member.name);
      });
    } catch (err: any) {
      if (err.code === "ALREADY_CLAIMED") toast("이미 가입된 회원이에요. 로그인 화면에서 PIN으로 들어가주세요");
      else if (err.code === "ROSTER_INVITE_EXPIRED") toast("초대 링크 유효 기간이 지났어요");
      else if (err.code === "INVALID_PIN") toast("PIN은 6자리 숫자로 설정해주세요");
      else toast(err.message);
    }
  }

  return (
    <form class="form" onSubmit={onSubmit}>
      <p class="card__lead" style="margin:0 0 6px">{member.name}님, 환영해요!</p>
      <div class="field">
        <span class="field__label">성별</span>
        <div class="chip-row">
          {(["male", "female"] as const).map((v) => (
            <button
              key={v}
              type="button"
              class={"chip" + (gender === v ? " chip--selected" : "")}
              onClick={() => pickGender(v)}
            >
              {v === "male" ? "남" : "여"}
            </button>
          ))}
        </div>
      </div>
      <div class="field">
        <span class="field__label">레벨 (선택)</span>
        {levelOpts.length === 0 ? (
          <p class="muted small" style="margin:0">성별을 먼저 선택해주세요.</p>
        ) : (
          <ChipPicker options={levelOpts} value={level} onChange={setLevel} />
        )}
        <span class="field__hint">나중에 프로필에서 바꿀 수 있어요.</span>
      </div>
      <div class="field">
        <span class="field__label">새 PIN</span>
        <input
          class="input"
          type="password"
          inputmode="numeric"
          autocomplete="new-password"
          maxLength={6}
          placeholder="새 PIN 6자리"
          value={pin}
          onInput={(e) => setPin((e.currentTarget as HTMLInputElement).value)}
        />
        <span class="field__hint">앞으로 이 PIN으로 로그인해요. 6자리 숫자.</span>
      </div>
      <div class="field">
        <span class="field__label">PIN 확인</span>
        <input
          class="input"
          type="password"
          inputmode="numeric"
          autocomplete="new-password"
          maxLength={6}
          placeholder="한번 더 입력"
          value={confirmPin}
          onInput={(e) => setConfirmPin((e.currentTarget as HTMLInputElement).value)}
        />
      </div>
      <PrivacyCheckbox id="roster-privacy-consent" checked={privacyOk} onChange={setPrivacyOk} />
      <button ref={submitRef} class="btn btn--primary btn--block" type="submit">가입 완료</button>
    </form>
  );
}

function openRosterClaim(member: any, slug: string, token: string) {
  openSheet("본인 정보 확인", mountPreact(
    <RosterClaimForm member={member} slug={slug} token={token} />,
  ));
}

function RosterJoinView({
  verified,
  slug,
  token,
}: {
  verified: any;
  slug: string;
  token: string;
}) {
  const members: any[] = Array.isArray(verified.members) ? verified.members : [];
  const [query, setQuery] = useState("");

  if (members.length === 0) {
    return (
      <Fragment>
        <ClubIntroCard verified={verified} />
        <div class="card">
          <p class="card__lead">이미 모두 가입을 완료했어요.</p>
          <p class="card__sub">이 링크로 가입할 수 있는 회원이 없어요. 로그인 화면으로 이동해 PIN으로 들어가주세요.</p>
          <button
            class="btn btn--primary btn--block"
            type="button"
            style="margin-top:8px"
            onClick={() => { window.location.href = `/c/${slug}/login`; }}
          >
            로그인으로 이동
          </button>
        </div>
      </Fragment>
    );
  }

  const q = query.trim().toLowerCase();
  const filtered = q ? members.filter((m) => (m.name || "").toLowerCase().includes(q)) : members;

  return (
    <Fragment>
      <ClubIntroCard verified={verified} />
      <div class="card">
        <p class="card__lead">본인 이름을 선택해주세요</p>
        <p class="card__sub">선택 후 성별·PIN을 설정하면 가입이 완료돼요. 이름이 없으면 운영진에게 문의해주세요.</p>
      </div>
      <input
        class="input"
        type="text"
        placeholder="이름 검색…"
        style="margin-bottom:8px"
        value={query}
        onInput={(e) => setQuery((e.currentTarget as HTMLInputElement).value)}
      />
      <div>
        {filtered.length === 0 ? (
          <p class="muted small">일치하는 이름이 없어요.</p>
        ) : (
          <div class="list list--compact">
            {filtered.map((m) => (
              <button
                key={m.member_id}
                type="button"
                class="list__item"
                onClick={() => openRosterClaim(m, slug, token)}
              >
                <div class="list__avatar">{(m.name || "?").slice(0, 1)}</div>
                <div class="list__main">
                  <p class="list__title">{m.name}</p>
                </div>
                <div class="list__trail"><Icon name="chevron" size={16} /></div>
              </button>
            ))}
          </div>
        )}
      </div>
    </Fragment>
  );
}

export async function renderRosterJoin(view: HTMLElement, token: string): Promise<void> {
  const slug = state.config.clubSlug;
  view.innerHTML = "";
  if (!slug || !token) {
    view.append(mountPreact(<div class="error-card">초대 링크가 올바르지 않아요.</div>));
    return;
  }

  const loading = document.createElement("div");
  loading.className = "loading";
  loading.textContent = "초대 확인 중…";
  view.append(loading);

  let verified: any;
  try {
    const base = (state.config.apiBase || DEFAULT_CONFIG.apiBase).replace(/\/$/, "");
    const url = `${base}/api/v1/public/clubs/${encodeURIComponent(slug)}/roster-join?token=${encodeURIComponent(token)}`;
    const resp = await fetch(url);
    const text = await resp.text();
    const payload = text ? safeJson(text) : null;
    if (!resp.ok) {
      const err = (payload && (payload.error || payload)) || {};
      const e: any = new Error(err.message || `요청 실패 (${resp.status})`);
      e.code = err.code;
      throw e;
    }
    verified = payload.data || payload;
  } catch (err: any) {
    view.innerHTML = "";
    if (err.code === "ROSTER_INVITE_EXPIRED") {
      view.append(mountPreact(
        <div class="error-card">
          <h2>링크가 만료됐어요</h2>
          <p>운영진에게 새 초대 링크를 요청해주세요.</p>
        </div>,
      ));
    } else if (err.code === "INVALID_ROSTER_INVITE") {
      view.append(mountPreact(
        <div class="error-card">
          <h2>유효하지 않은 링크예요</h2>
          <p>링크를 다시 확인하거나 운영진에게 문의해주세요.</p>
        </div>,
      ));
    } else {
      view.append(mountPreact(<div class="error-card">{err.message}</div>));
    }
    return;
  }

  view.innerHTML = "";
  view.append(mountPreact(<RosterJoinView verified={verified} slug={slug} token={token} />));
}

// =====================================================================
// renderMemberLogin
// =====================================================================

function MemberLoginForm() {
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");

  async function onSubmit(e: Event) {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedPin = pin.trim();
    if (!trimmedName) { toast("이름을 입력해주세요"); return; }
    if (!/^\d{6}$/.test(trimmedPin)) { toast("PIN 6자리를 입력해주세요"); return; }
    try {
      const slug = state.config.clubSlug;
      const res = await api(`/api/v1/clubs/${slug}/member-login`, {
        method: "POST", body: { name: trimmedName, pin: trimmedPin },
      });
      state.session = res.auth;
      if (res.club) state.club = res.club;
      persist();
      toast("로그인됐어요");
      const next = state.pendingRoute;
      state.pendingRoute = null;
      navigate(next || { name: "home" });
    } catch (err: any) {
      if (err.code === "INVALID_CREDENTIALS") toast("이름 또는 PIN이 맞지 않아요");
      else if (err.code === "PIN_LOCKED") toast("여러 번 틀렸어요. 잠시 후 다시 시도해주세요");
      else if (err.code === "PIN_NOT_SET") toast("먼저 초대 링크로 가입을 완료해주세요");
      else toast(err.message);
    }
  }

  return (
    <form class="form" onSubmit={onSubmit}>
      <div class="field">
        <span class="field__label">이름</span>
        <input
          class="input"
          type="text"
          autocomplete="name"
          placeholder="이름"
          value={name}
          onInput={(e) => setName((e.currentTarget as HTMLInputElement).value)}
        />
      </div>
      <div class="field">
        <span class="field__label">PIN</span>
        <input
          class="input"
          type="password"
          inputmode="numeric"
          autocomplete="current-password"
          maxLength={6}
          placeholder="PIN 6자리"
          value={pin}
          onInput={(e) => setPin((e.currentTarget as HTMLInputElement).value)}
        />
      </div>
      <button class="btn btn--primary btn--block" type="submit">로그인</button>
    </form>
  );
}

export async function renderMemberLogin(view: HTMLElement): Promise<void> {
  view.innerHTML = "";
  view.append(mountPreact(
    <div class="section">
      <div class="section__title"><h3>회원 로그인</h3></div>
      <p class="muted" style="margin-bottom:4px">가입 시 등록한 이름과 PIN을 입력해주세요.</p>
      <p class="muted small" style="margin:0 0 12px">
        동명이인이 있는 클럽이면 "김민수A"처럼 본인 구분자까지 그대로 입력해주세요.
      </p>
      <MemberLoginForm />
    </div>,
  ));
}
